import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { resolveCancellationPolicy, policyTierMessageValues } from '../../../util/cancellationPolicy';
import { richText } from '../../../util/richText';
import { isOneSessionTerm } from '../../../util/termSchedule';

import CustomListingFields from '../CustomListingFields';

import css from './ClassDetail.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

// A "Label: text" row for the Phase 2 requirements fields; hides when the note wasn't provided.
const LeadInRow = ({ labelId, text }) =>
  typeof text === 'string' && text.trim() !== '' ? (
    <p className={css.aboutRow}>
      <span className={css.aboutLead}>
        <FormattedMessage id={labelId} />
      </span>{' '}
      {text}
    </p>
  ) : null;

/**
 * "About this activity": the description plus the wizard's requirements fields (what kids learn,
 * materials, equipment) as bold lead-in rows, the resolved cancellation policy, and any remaining
 * Console-defined listing fields (config-driven, so a field toggled visible in Console appears here
 * without code -- same pipeline the search rail uses).
 */
const ClassAbout = props => {
  const { listing, config, listingConfig, intl } = props;
  const { description, publicData = {}, metadata = {} } = listing.attributes || {};
  const { whatKidsLearn, materials, equipmentProvided } = publicData;

  // Listing override -> provider default -> per-format system default; always normalized tiers.
  const policy = resolveCancellationPolicy({ listing, provider: listing.author });
  const policyText = policy
    ? policy.tiers
        .map(tier =>
          intl.formatMessage(
            {
              id:
                tier.refundPercentage === 100
                  ? 'ClassDetailPage.policyTierFull'
                  : tier.refundPercentage === 0
                  ? 'ClassDetailPage.policyTierNone'
                  : 'ClassDetailPage.policyTierPartial',
            },
            policyTierMessageValues(intl, tier)
          )
        )
        .join(' ')
    : null;

  const descriptionText =
    typeof description === 'string' && description.trim() !== ''
      ? richText(description, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          longWordClass: css.longWord,
          breakChars: '/',
        })
      : null;

  return (
    <section className={css.card}>
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="ClassDetailPage.aboutTitle" />
      </h2>
      {descriptionText ? <p className={css.aboutDescription}>{descriptionText}</p> : null}

      <LeadInRow labelId="ClassDetailPage.whatKidsLearn" text={whatKidsLearn} />
      <LeadInRow labelId="ClassDetailPage.materials" text={materials} />
      <LeadInRow labelId="ClassDetailPage.equipmentProvided" text={equipmentProvided} />
      <LeadInRow labelId="ClassDetailPage.cancellationPolicy" text={policyText} />

      <div className={css.aboutCustomFields}>
        <CustomListingFields
          // A one-session term (the old one-off workaround) lists its format as a drop-in, not
          // "Term enrollment" -- display only; the stored classFormat is unchanged.
          publicData={
            isOneSessionTerm(publicData)
              ? { ...publicData, classFormat: 'single-session' }
              : publicData
          }
          metadata={metadata}
          listingFieldConfigs={listingConfig.listingFields}
          categoryConfiguration={config.categoryConfiguration}
          intl={intl}
        />
      </div>
    </section>
  );
};

export default ClassAbout;
